import * as React from "react";
import { inject, observer } from "mobx-react";
import {AppStore, PostscriptStore} from "Stores/index";
import {
    CellContext,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { POSTSCRIPT_TERM_OPTIONS, PostscriptTerm, PostscriptTermFunction } from "Stores/superStore";
import * as _ from "lodash";
import { toJS } from "mobx";
import { CreatableWrapper, LabelValueOption, SelectWrapper } from "Components/Common/SelectWrapper";
import {CrudModal} from "Components/Common/CrudModal";
import {useEffect, useState} from "react";
import {ActionWrapper} from "Components/Common/ActionWrapper";
import PageTitle from "Components/Common/PageTitle";
import {useInternalContext} from "@/contexts/internal";
import { usePostscriptTermOptions } from "@/hooks/terms";


const stringToOption = (str: string) => ({
    value: str,
    label: str,
});

interface RegexTerm {name: string, term: string}

const REGEX_LITERALS = ["{", "[", "|", "^", ".", "*", "+", "=", "?", "$"];

interface PostscriptTermsListProps {
    postscriptStore?: PostscriptStore
    appStore?: AppStore
}

interface PostscriptTermsListState {
    showModal: boolean
    selectedTerm: PostscriptTerm | undefined
    formTermValue: string
    formExpressionValue: string
    inputFormExpressionValue: string
    formFunctionValue: {label: string, value: string}
    testText: string
    testMatches: undefined | string[]
    isRunningExpressionTest: boolean
    simulateResults: RegexTerm[] | undefined
    postscriptTermSource: LabelValueOption | undefined
}

export const PostscriptTermsList = inject("postscriptStore", "appStore")(observer((props: PostscriptTermsListProps) => {
    const [state, setState] = useState<PostscriptTermsListState>({
        showModal: false,
        selectedTerm: undefined,
        formTermValue: "",
        formExpressionValue: "",
        inputFormExpressionValue: "",
        formFunctionValue: stringToOption("search"),
        testText: "",
        testMatches: undefined,
        isRunningExpressionTest: false,
        simulateResults: undefined,
        postscriptTermSource: undefined,
    });
    const {options: postscriptTermsOptions} = usePostscriptTermOptions();
    const internalContext = useInternalContext();

    const columns: ColumnDef<PostscriptTerm>[] = [{
        header: "Term",
        accessorKey: "term",
        size: 100,
    }, {
        header: "Expression",
        accessorKey: "expression",
        size: 300,
    }, {
        header: "Postscript Terms Source",
        accessorKey: "postscript_terms_source",
        size: 100,
        cell: (props: CellContext<PostscriptTerm, unknown>) => {
            const {source} = props.row.original;
            return source ? source : "";
        }
    }, {
        header: "Function",
        accessorKey: "function",
        size: 100,
    }, {
        header: "Actions",
        cell: (props) => renderActionButton(props.row.original)
    }];

    const regexTestColumns: ColumnDef<RegexTerm>[] = [{
        header: "Course Name",
        accessorKey: "name",
    }, {
        header: "Term",
        accessorKey: "term",
        size: 50,
    }];

    useEffect(() => {
        if (!props.postscriptStore) return;
        props.postscriptStore.fetchTerms();
        props.postscriptStore.fetchDefaultExpressions();
        internalContext.setRenderActionButton(renderCreateButton());

        return () => {
            internalContext.setRenderActionButton(null);
        };
    }, [props.postscriptStore]);

    const renderCreateButton = () => (
        <div className="actions">
            <Button onClick={handleAddClick} id="postscript-terms-create-term-button">create term mapper</Button>
        </div>
    );

    const handleEditClick = (term: PostscriptTerm) => {
        setState({
            ...state,
            showModal: true,
            selectedTerm: term,
            formTermValue: term.term,
            formExpressionValue: term.expression,
            formFunctionValue: stringToOption(term.function),
            postscriptTermSource: term.source ? stringToOption(term.source) : undefined,
        });
    };

    const handleDeleteClick = (term: PostscriptTerm) => {
        if (!props.postscriptStore) return;
        props.postscriptStore.deleteTerm(term.id);
    };

    const handleAddClick = () => {
        setState({
            ...state,
            showModal: true,
            selectedTerm: undefined,
            formTermValue: "",
            formExpressionValue: "",
            formFunctionValue: stringToOption("search"),
            isRunningExpressionTest: false,
            simulateResults: undefined,
            postscriptTermSource: undefined,
        });
    };

    const handleModalClose = () => {
        setState({
            ...state,
            selectedTerm: undefined,
            showModal: false,
            testMatches: [],
            testText: "",
            formExpressionValue: "",
            formFunctionValue: stringToOption("search"),
            formTermValue: "",
            isRunningExpressionTest: false,
            simulateResults: undefined,
        });
    };

    const handleTermChange = (ev: any) => {
        setState({...state, formTermValue: ev.target.value});
    };

    const handleExpressionChange = (option: any) => {
        if (option.value.length === 0) {
            setState({...state, formExpressionValue: option.value, testMatches: undefined});
            return;
        }

        if (state.formFunctionValue.value === "replace") {
            if (!option.value.includes("(") || !option.value.includes(")")) {
                addError(new Error("Invalid regular expression"), "Invalid Expression - It must contain ( and )");
                return;
            }
            const found = REGEX_LITERALS.find(literal => option.value.includes(literal));
            if (!found) {
                addError(new Error("Invalid regular expression"), "Invalid Expression - not enough regex literals");
                return;
            }

            try {
                new RegExp(option.value.replace("(?i)", ""));
            } catch (e) {
                addError(e, e.message);
                return;
            }
        }
        setState({...state, formExpressionValue: option.value, testMatches: undefined});
    };

    const handleInputExpressionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, inputFormExpressionValue: e.target.value});
    };

    const addError = (e: Error, customMessage: string) => {
        if (props.appStore) {
            props.appStore.addError(e, customMessage);
        }
    };

    const handleFunctionChange = (option: any) => {
        setState({...state, formFunctionValue: option});
    };

    const handlePostscriptTermSource = (option: LabelValueOption | undefined) => {
        const update: Partial<PostscriptTermsListState> = { postscriptTermSource: option };
        if (option?.value === "CANVAS") {
            update.formFunctionValue = stringToOption("search");
        }
        setState(s => ({ ...s, ...update }));
    };

    const handleTestTextChange = (ev: any) => {
        setState({...state, testText: ev.target.value, testMatches: undefined});
    };

    const getExpressionAddPromptText = (val: string) => (`Add a custom term: ${val}`);

    const convertRegex = (regex: string): RegExp => regex.includes("(?i)")
        ? new RegExp(regex.replace("(?i)", ""), "i")
        : new RegExp(regex);

    const tryDetectExpression = () => {
        if(state.testText && state.formExpressionValue){
            // See if currently selected expression matches. If it does, skip iterating through expressions list
            try{
                const exp = convertRegex(state.formExpressionValue);
                if(exp.test(state.testText)){
                    setState({...state, testMatches:[state.formExpressionValue]});
                    return;
                }
            } catch(e){
                // do nothing,
            }
        }
        const stringsToMatch = state.testText.split(",");
        const expressions = _get_unused_expressions();
        const matches: string[] = [];

        for(let idxExp = expressions.length - 1; idxExp > -1; idxExp--){
            const expression = expressions[idxExp];
            const regex = convertRegex(expression);
            let works = true;
            for(let idxMatch = stringsToMatch.length - 1; idxMatch > -1; idxMatch--){
                const test = stringsToMatch[idxMatch];
                if(!regex.test(test)){
                    works = false;
                    break;
                }
            }
            if(works){
                matches.push(expression);
            }
        }
        switch(matches.length) {
            case 0:
                setState({...state, testMatches: matches});
                break;
            case 1:
                setState({...state, formExpressionValue: matches[0], testMatches: matches});
                break;
            default:
                props.postscriptStore!.defaultExpressions = _.sortBy(
                    props.postscriptStore!.defaultExpressions.slice(),
                    [(expression) => _.findIndex(matches, match => match === expression) > -1 ? 0 : 1]);
                setState({...state, formExpressionValue:"", testMatches: matches});
                break;
        }
    };

    const testInput = () => {
        const {formTermValue, formExpressionValue, formFunctionValue} = state;
        setState({...state, isRunningExpressionTest: true});

        props.postscriptStore!.testRegex(formTermValue, formExpressionValue, formFunctionValue.value).then(resp => {
            setState({
                ...state,
                simulateResults: resp.data.data.results,
                isRunningExpressionTest: false,
            });
        });
    };

    const testInputFormExpression = async () => {
        const {formTermValue, inputFormExpressionValue, formFunctionValue} = state;
        setState({...state, isRunningExpressionTest: true});
        props.postscriptStore!.testRegex(formTermValue, inputFormExpressionValue, formFunctionValue.value).then(resp => {
            setState({
                ...state,
                simulateResults: resp.data.data.results,
                isRunningExpressionTest: false,
            });
        });
    };

    const _get_unused_expressions = () => {
        const ret = toJS(props.postscriptStore!.defaultExpressions).filter((expression: string) => {
            return !props.postscriptStore!.terms.find((term: PostscriptTerm) => term.expression === expression);
        });
        if(state.selectedTerm){
            ret.unshift(state.selectedTerm!.expression);
        }
        return ret;
    };

    const getExpressionOptions = () => {
        const defaultExpressions = _get_unused_expressions();
        return defaultExpressions.map(stringToOption);
    };

    const getExpressionValid = (): boolean => {
        if(state.selectedTerm !== undefined && state.selectedTerm!.expression === state.formExpressionValue){
            return true;
        }
        return !toJS(props.postscriptStore!.terms).find((term: PostscriptTerm) => (term.expression === state.formExpressionValue));
    };

    const isValid = (): boolean => {
        if (state.formFunctionValue.value === "search" && state.formTermValue.includes("\\1")) {
            return false;
        }

        if (state.formFunctionValue.value === "replace" && !state.formTermValue.includes("\\1")) {
            return false;
        }
        const allFieldsFilled = !!(state.formTermValue && state.formExpressionValue && state.formFunctionValue);
        if(state.selectedTerm){
            return (allFieldsFilled);
        }
        return allFieldsFilled && getExpressionValid();
    };

    const saveTerm = () => {
        if(!isValid() || !props.postscriptStore) return;

        const {formTermValue, formExpressionValue, formFunctionValue, postscriptTermSource} = state;
        const toSend: Partial<PostscriptTerm> = {
            term: formTermValue,
            expression: formExpressionValue,
            function: formFunctionValue.value as PostscriptTermFunction,
            source: postscriptTermSource ? postscriptTermSource.value : undefined,
        };

        (state.selectedTerm
            ? props.postscriptStore.updateTerm((state.selectedTerm as never as PostscriptTerm).id, toSend)
            : props.postscriptStore.createTerm(toSend))
            .then(() => {
                Promise.all([
                    props.postscriptStore!.fetchTerms(),
                    props.postscriptStore!.fetchDefaultExpressions(),
                ]).then(handleModalClose);
            });
    };

    const renderActionButton = (data: PostscriptTerm) => {
        const actionProps = {
            data,
            onEdit: handleEditClick,
            onDelete: handleDeleteClick
        };

        return <ActionWrapper {...actionProps} />;
    };

    const renderExpressionMatchResultMessage = () => {
        if(state.testMatches === undefined || !state.testText) return null;
        let message = "";
        if (state.testMatches.length === 0){
            message = "No matches";
        } else if (state.testMatches.length > 1){
            message = `${state.testMatches.length} matches`;
        }

        return (
            <Row>
                { message }
            </Row>
        );
    };

    const renderModalContent = () => {
        return (
            <>
                <Row className="margin-top">
                    {renderForm()}
                    <Col md={6} className="results">
                        <Form.Label>Simulated Term Calculations</Form.Label>
                        <ModalContentReactTable
                            columns={regexTestColumns}
                            data={state.simulateResults ?? []}
                            noDataText={state.simulateResults === undefined ? 'Click "Find Course Matches" to see results' : "No Results"}/>
                        <p className="small">*Table shows the terms that would be set following the next postscript
                            run</p>
                    </Col>
                </Row>
            </>
        );
    };

    const renderForm = () => {
        return (
            <Col>
                <Row>
                    <Col>
                        <Form.Group>
                            <Form.Label>Term</Form.Label>
                            <Form.Control onChange={ handleTermChange } value={ state.formTermValue } placeholder="Enter a term"/>
                            {state.formFunctionValue.value === "replace" && <Form.Text className="text-muted">
                                Term must contain a &quot;\1&quot;
                            </Form.Text>}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group>
                            <Form.Label>Postscript Terms Source</Form.Label>
                            <SelectWrapper
                                options={ postscriptTermsOptions}
                                value={ state.postscriptTermSource }
                                onChange={ handlePostscriptTermSource }
                                isSearchable={ false }
                                isClearable={ true }
                                openMenuOnFocus={ true }
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <fieldset className="fieldset fullwidth">
                            <Form.Group className="select-wrapper-group">
                                <label>Expression</label>
                                <CreatableWrapper
                                    options={getExpressionOptions()}
                                    value={ state.formExpressionValue ? stringToOption(state.formExpressionValue) : undefined}
                                    onChange={ handleExpressionChange }
                                    formatCreateLabel={ getExpressionAddPromptText }
                                    placeholder="Select or create a value"
                                    isClearable={false}
                                    isValidNewOption={getExpressionValid}/>
                                <Form.Control.Feedback type="invalid">This expression is already in use</Form.Control.Feedback>
                            </Form.Group>
                            <Row className="margin-top justify-content-end text-end">
                                <Col>
                                    <Button
                                        onClick={ testInput }
                                        disabled={!(state.formExpressionValue && state.formFunctionValue && state.formTermValue)}>
                                        {state.isRunningExpressionTest ? <Spinner animation="border"/> : "Find Course Matches"}
                                    </Button>
                                </Col>
                            </Row>
                            <Form.Group>
                                <Form.Label>Test a Regular Expression</Form.Label>
                                <Form.Control value={state.inputFormExpressionValue} onChange={handleInputExpressionChange} placeholder="Test a Regular Expression"/>
                                <Form.Text className="text-muted">For testing purpose only, if you want to save this expression please paste it in the previous field.</Form.Text>
                            </Form.Group>
                            <Row className="margin-top justify-content-end text-end">
                                <Col>
                                    <Button
                                        onClick={ testInputFormExpression }
                                        disabled={!(state.inputFormExpressionValue && state.formTermValue)}>
                                        {state.isRunningExpressionTest ? <Spinner animation="border"/> : "Find Course Matches"}
                                    </Button>
                                </Col>
                            </Row>
                            <Form.Label>Test a Course Name</Form.Label>
                            <Form.Control
                                onChange={ handleTestTextChange }
                                value={ state.testText }
                                isValid={
                                    !!state.formExpressionValue
                                    && !!state.testText
                                    && convertRegex(state.formExpressionValue).test(state.testText)}
                                placeholder="ex: [PHAR 1234] Intro Class Fall 2020"
                            />
                            <Form.Control.Feedback>Matches</Form.Control.Feedback>
                            {renderExpressionMatchResultMessage()}
                            <div className="justify-content-end text-end">
                                <Button
                                    onClick={ tryDetectExpression }
                                    disabled={ !state.testText }>
                                    Find Matching Expression
                                </Button>
                            </div>
                        </fieldset>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <label>Function</label>
                        {state.postscriptTermSource?.value === "CANVAS" ? (
                            <div style={{ padding: "0.375rem 0.75rem", minHeight: "38px", border: "1px solid #ced4da", borderRadius: "0.25rem", background: "#e9ecef" }}>
                                {state.formFunctionValue.label}
                            </div>
                        ) : (
                            <SelectWrapper
                                options={ POSTSCRIPT_TERM_OPTIONS.map(stringToOption) }
                                value={ state.formFunctionValue }
                                onChange={ handleFunctionChange }
                                isSearchable={ false }
                                isClearable={ false }
                                openMenuOnFocus={ true }
                                isDisabled={false}
                            />
                        )}
                    </Col>
                </Row>
            </Col>
        );
    };

    if (!props.postscriptStore) return null;

    return (
        <>
            <PageTitle pageTitle={"Postscript Terms"}/>
            <div className={"list"}>
                <PostscriptTermsReactTable columns={columns} data={props.postscriptStore.terms.slice()}/>
            </div>
            {state.showModal
                ? <CrudModal
                    typeLabel={"Term Mapping"}
                    isEdit={!!state.selectedTerm}
                    renderForm={renderModalContent}
                    onClose={handleModalClose}
                    onSave={saveTerm}
                    isDisabled={!isValid() || props.postscriptStore.isUpdatingTerms}
                    showSpinner={props.postscriptStore.isUpdatingTerms} /> : null}
        </>
    );
}));

interface PostscriptTermsReactTableProps {
    columns: ColumnDef<PostscriptTerm>[]
    data: PostscriptTerm[]
}

function PostscriptTermsReactTable({columns, data}: PostscriptTermsReactTableProps) {
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <>
            <Table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
                {table.getRowModel().rows.length === 0 && (<tr><td colSpan={4} className="text-center">No Postscript Terms</td></tr>)}
                </tbody>
            </Table>
            <div className={"pagination-bottom"}>
                <div className={"pagination"}>
                    <div className={"previous d-grid gap-2"}>
                        <Button disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}>Previous</Button>
                    </div>
                    <div className={"center"}>
                        <span className={"page-info"}>
                            Page
                            <div className={"page-jump"}>
                                <Form.Control
                                    type="number"
                                    value={table.getState().pagination.pageIndex + 1}
                                    onChange={e => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        const pageIndex = table.getState().pagination.pageIndex;
                                        if (page > pageIndex) {
                                            if (table.getCanNextPage()) {
                                                table.setPageIndex(page);
                                            }
                                        } else {
                                            if (table.getCanPreviousPage()) {
                                                table.setPageIndex(page);
                                            }
                                        }
                                    }}/>
                            </div>
                            of {" "}
                            <span className={"total-pages"}>{table.getPageCount()}</span>
                        </span>
                    </div>
                    <div className={"next d-grid gap-2"}>
                        <Button disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}>Next</Button>
                    </div>
                </div>
            </div>
        </>
    );
}

interface ModalContentReactTableProps {
    columns: ColumnDef<RegexTerm>[]
    data: RegexTerm[]
    noDataText: string
}

function ModalContentReactTable({columns, data, noDataText}: ModalContentReactTableProps) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const table = useReactTable({
        columns,
        data,
        state: {
            sorting,
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting
    });

    return (
        <div className={"list"}>
            <Table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : (
                                        <div
                                            className={header.column.getCanSort() ? "cursor-pointer " + (header.column.getIsSorted() || "") : ""}
                                            onClick={header.column.getToggleSortingHandler()}>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </div>
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
                {table.getRowModel().rows.length === 0 && (
                    <tr>
                        <td colSpan={2}>{noDataText}</td>
                    </tr>
                )}
                </tbody>
            </Table>
            <div className={"pagination-bottom mt-2"}>
                <div className={"pagination"}>
                    <div className={"previous d-grid gap-2"}>
                        <Button disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}>Previous</Button>
                    </div>
                    <div className={"center"}>
                        <span className={"page-info"}>
                            Page
                            <div className={"page-jump"}>
                                <Form.Control
                                    type="number"
                                    value={table.getState().pagination.pageIndex + 1}
                                    onChange={e => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        const pageIndex = table.getState().pagination.pageIndex;
                                        if (page > pageIndex) {
                                            if (table.getCanNextPage()) {
                                                table.setPageIndex(page);
                                            }
                                        } else {
                                            if (table.getCanPreviousPage()) {
                                                table.setPageIndex(page);
                                            }
                                        }
                                    }}/>
                            </div>
                            of{" "}
                            <span className={"total-pages"}>{table.getPageCount()}</span>
                        </span>
                    </div>
                    <div className={"next d-grid gap-2"}>
                        <Button disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}>Next</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}